export const RESUME_OUTCOME = Object.freeze({
	COMPLETED: 1,
	RETRY: 0,
	LOST: -1,
	HANDED_OFF: 2,
	NEEDS_ATTENTION: 3,
	RECOVERY_BLOCKED: 4
} as const);

export type ResumeOutcome = (typeof RESUME_OUTCOME)[keyof typeof RESUME_OUTCOME];

export interface ResumeCancellationAttempt {
	readonly controller: AbortController;
	readonly signal: AbortSignal;
	release(): void;
}

export interface ResumeCancellationRegistry {
	start(sessionId: string, externalSignal?: AbortSignal): ResumeCancellationAttempt;
	run<Outcome>(
		sessionId: string,
		externalSignal: AbortSignal | undefined,
		produceOutcome: (attempt: ResumeCancellationAttempt) => Promise<Outcome>
	): Promise<Outcome>;
	has(sessionId: string): boolean;
}

export function createResumeCancellationRegistry(): ResumeCancellationRegistry {
	const controllersBySessionId = new Map<string, AbortController>();

	const registry: ResumeCancellationRegistry = {
		start(sessionId, externalSignal) {
			controllersBySessionId.get(sessionId)?.abort();
			const controller = new AbortController();
			controllersBySessionId.set(sessionId, controller);
			const abortController = (): void => controller.abort();

			if (externalSignal?.aborted) {
				controller.abort();
			} else {
				externalSignal?.addEventListener("abort", abortController, {
					once: true
				});
			}

			return {
				controller,
				signal: controller.signal,
				release() {
					externalSignal?.removeEventListener("abort", abortController);
					if (controllersBySessionId.get(sessionId) === controller) {
						controllersBySessionId.delete(sessionId);
					}
				}
			};
		},
		async run(sessionId, externalSignal, produceOutcome) {
			const attempt = registry.start(sessionId, externalSignal);
			try {
				return await produceOutcome(attempt);
			} finally {
				attempt.release();
			}
		},
		has(sessionId) {
			return controllersBySessionId.has(sessionId);
		}
	};

	return registry;
}
